import {StackDisposable, CallableDisposableStack} from "./disposable.js";
export {StackDisposable, CallableDisposableStack, CallableDisposableStack as callableDisposableStack};

export type Fn<P extends unknown[], R> = (...args: P) => R;

export type HandlerRegObject<F extends (...params: any[]) => any> = {
    on(handler: F): Disposable;
    once(handler: F): Disposable;
    off(handler: F): boolean;
    connect(handler: F): Disposable;
    connectWithId(id: string, handler: F): Disposable;
    //[Symbol.asyncIterator](): AsyncIterator<Parameters<F>>;
    handlers: {[id: string]: F};
    readonly isConnected: boolean;
};
export function makeHandlerRegObject<F extends (...params: any[]) => any, IN extends object = object>(input: IN): IN & HandlerRegObject<F> {
    let index = 0;
    const fun = input as IN & HandlerRegObject<F>;
    fun.handlers = {};
    fun.connectWithId = (id: string, handler: F): Disposable => {
        fun.handlers['_' + id] = handler;
        const wFun = new WeakRef(fun), wHandler = new WeakRef(handler);
        return {
            [Symbol.dispose]() {
                const _fun = wFun.deref(), _handler = wHandler.deref();
                if (_fun && _fun.handlers['_' + id] === _handler) delete _fun.handlers['_' + id];
            }
        }
    }
    fun.on = fun.connect = (handler: F): Disposable => {
        return fun.connectWithId('$' + (++index), handler);
    }
    fun.once = (handler: F): Disposable => {
        const ref = fun.connectWithId('$' + (++index), function(...args: Parameters<F>): ReturnType<F> {
            ref[Symbol.dispose]();
            return handler(...args);
        } as F);
        return ref;
    }
    Object.defineProperty(fun, 'isConnected', {
        get(): boolean {
            for (const _ in this.handlers)
                return true;
            return false;
        }
    })
    fun.off = (handler: F): boolean => {
        for (const k in fun.handlers) {
            if (fun.handlers[k] === handler) {
                delete fun.handlers[k];
                return true;
            }
        }
        return false;
    }
    /*fun[Symbol.asyncIterator] = function() {
        var events : any[] = [];
        var resolve;
        var handler = fun.connect((...args: Parameters<F>): ReturnType<F> => {
            events.push();
        });
        return {
            next: function() {
                return new Promise((resolve, reject) => {

                });
            }
        };
    };*/
    return fun;
}
export type SignalFunc<P extends unknown[], R> = HandlerRegObject<Fn<P, R>> & {
    (...params: P): R[];
    collect(...params: P): R[];
    emit(...params: P): void;
    asyncCollect (...params: P): Promise<R[]>;
    collectFirst(...params: P): R | undefined;
};
export type Signal<F extends (...args: any[]) => any> = SignalFunc<Parameters<F>, ReturnType<F>>;
export type ArraySignalFunc<P extends unknown[], R extends unknown[]> = SignalFunc<P, R> & {
    flatCollect(...params: P): R;
};
export function arraySignal<F extends (...args: any) => any[]>() : ArraySignalFunc<Parameters<F>, ReturnType<F>> {
    type P = Parameters<F>; type R = ReturnType<F>;
    var fun = <ArraySignalFunc<P, R>>(signal<F>());
    fun.flatCollect = (...r: P): R => {
        return <R>fun(...r).flat(1);
    };
    return fun;
}
export function signal<F extends (...args: any[]) => any>() : SignalFunc<Parameters<F>, ReturnType<F>> {
    type P = Parameters<F>; type R = ReturnType<F>;
    var fun = <SignalFunc<P, R>>((...r: P): R[] => {
        return fun.collect(...r);
    });
    makeHandlerRegObject(fun);
    fun.emit = (...r: P): void => {
        for(const handler of Object.values(fun.handlers)) {
            handler(...r);
        }
    }
    fun.collectFirst = (...r: P): R | undefined => {
        for(const handler of Object.values(fun.handlers)) {
            const result = handler(...r);
            if (result !== undefined) return result;
        }
        return undefined;
    }
    fun.collect = (...r: P): R[] => {
        var result : R[] = [];
        for(const handler of Object.values(fun.handlers)) {
            result.push(handler(...r));
        }
        return result;
    }
    fun.asyncCollect = (...r: P): Promise<R[]> => {
        return Promise.all(fun.collect(...r));
    };
    return fun;
}

export function lazyInit<C,R>(ctor: (this: C, self: C) => R) {
    return function (value: any, ctx: ClassGetterDecoratorContext<C, R>) {
        if (ctx.kind === "getter") {
            const instanceKey = ctx.name.toString() + "__";
            return function(): R {
                if (!(instanceKey in this)) this[instanceKey] = ctor.call(this, this) as R;
                return this[instanceKey];
            };
        };
    }
}

export function forceSignal(obj, signalKey) {
    if (!(signalKey in obj)) obj[signalKey] = signal();
    return obj[signalKey];
}


export function observable<This, Value>(value, ctx: ClassAccessorDecoratorContext<This, Value>): { get(): Value; set(v: Value): void; }
export function observable<This, Value>(value, ctx: ClassFieldDecoratorContext<This, Value>): (initialValue: Value) => Value;
export function observable(value, ctx) {
    const signalKey = ctx.name.toString() + "$";
    ctx.addInitializer(function() {
        forceSignal(this, signalKey);
    });
    if (ctx.kind === "field") {
        return function (initialValue) {
            const privateKey = ctx.name.toString() + "_";
            Object.defineProperties(this, {
                [ctx.name]: {
                    get() { return this[privateKey]; },
                    set(v) { var oldV = this[privateKey]; this[privateKey] = v; forceSignal(this, signalKey)(oldV, v); }
                }
            });
            return initialValue;
        };
    } else if (ctx.kind === "accessor") {
        return {
            get() {
                return value.get.call(this);
            },
            set(v: unknown) {
                var oldV = value.get.call(this); value.set.call(this, v); this[signalKey](oldV, v);
            }
        };
    }
}

export function observableSignal<Observed, Prop extends string & keyof Observed, Target = Observed[Prop]>(obj: Observed, observableName: Prop): SignalFunc<[Target, Target], void> {
    return forceSignal(obj, observableName + '$');
}
